import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSettingsData } from '../../hooks/useData';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationTray from '../Notifications/NotificationTray';
import { 
  HomeIcon, 
  BriefcaseIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  DocumentCheckIcon as CertificateIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  LifebuoyIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

interface DashboardProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  badge?: string;
}

// Primary Navigation - Core business workflow
const primaryNavigationAll: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Quotes', href: '/dashboard/quotes', icon: DocumentTextIcon },
  { name: 'Jobs', href: '/dashboard/jobs', icon: BriefcaseIcon },
  { name: 'Certificates', href: '/dashboard/certificates', icon: CertificateIcon },
  { name: 'Invoices', href: '/dashboard/invoices', icon: CreditCardIcon },
];

// Secondary Navigation - Data management. On the Solo plan we hide the
// Price Book (engineers-only feature) and Communication hub (team chat).
const secondaryNavigationAll: NavigationItem[] = [
  { name: 'Clients', href: '/dashboard/clients', icon: UserGroupIcon },
  { name: 'Sites', href: '/dashboard/sites', icon: BuildingOfficeIcon },
  { name: 'Price Book', href: '/dashboard/price-book', icon: DocumentTextIcon },
  { name: 'Communication', href: '/dashboard/communication', icon: ChatBubbleLeftRightIcon },
];

const SOLO_HIDDEN_HREFS = new Set<string>([
  '/dashboard/price-book',
  '/dashboard/communication',
]);

// Footer Navigation - Account actions
const footerNavigation: NavigationItem[] = [
  { name: 'Logout', href: '/logout', icon: ArrowRightOnRectangleIcon },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function Dashboard({ children }: DashboardProps) {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { data: settingsData } = useSettingsData();
  const { unreadCount } = useNotifications();
  const profilePhotoUrl = (settingsData as any)?.user?.profilePhotoUrl as string | undefined;

  const isActive = (path: string) => {
    // Special-case the root dashboard path so it doesn't stay active on subpages
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSoloPlan = user?.plan === 'SOLO';
  const primaryNavigation = primaryNavigationAll;
  const secondaryNavigation = isSoloPlan
    ? secondaryNavigationAll.filter((i) => !SOLO_HIDDEN_HREFS.has(i.href))
    : secondaryNavigationAll;

  const displayName = user?.name || user?.email || 'User';
  const isLongName = displayName.length > 18;
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await logout();
  };

  return (
    <div className="app-shell">
      <div className="app-container">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="sidebar-logo-icon">
                <img
                  src="/pureblacklogo.png"
                  alt="Certs logo"
                  className="sidebar-logo-img"
                />
              </div>
              <span className="sidebar-logo-text">Certs</span>
            </div>

            <div className="sidebar-header-actions">
              <button
                type="button"
                onClick={() => setIsNotificationOpen((v) => !v)}
                className="sidebar-header-btn"
                title="Notifications"
                aria-label="Notifications"
              >
                <BellIcon className="w-5 h-5" aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="sidebar-header-btn-badge" aria-label={`${unreadCount} unread`}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {isNotificationOpen && (
                <>
                  <div
                    className="sidebar-notification-backdrop"
                    onClick={() => setIsNotificationOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="sidebar-notification-panel">
                    <NotificationTray
                      isOpen={isNotificationOpen}
                      onClose={() => setIsNotificationOpen(false)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <nav className="sidebar-nav">
            <div className="nav-group">
              <div className="nav-group-header">
                <span className="nav-group-title">WORKSPACE</span>
              </div>
              <div className="nav-group-items">
                {primaryNavigation.map((item: NavigationItem) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={item.name}
                    className={classNames(
                      'nav-item',
                      isActive(item.href) ? 'active' : ''
                    )}
                  >
                    <item.icon aria-hidden="true" />
                    <span>{item.name}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                  </Link>
                ))}
              </div>
            </div>

            <div className="nav-group">
              <div className="nav-group-header">
                <span className="nav-group-title">CLIENT HUB</span>
              </div>
              <div className="nav-group-items">
                {secondaryNavigation.map((item: NavigationItem) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={item.name}
                    className={classNames(
                      'nav-item',
                      isActive(item.href) ? 'active' : ''
                    )}
                  >
                    <item.icon aria-hidden="true" />
                    <span>{item.name}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                  </Link>
                ))}
              </div>
            </div>
          </nav>


          {/* Sidebar Footer */}
          <div className="sidebar-footer">
            <div className="sidebar-profile sidebar-profile-card">
              <button
                type="button"
                className="sidebar-profile-trigger"
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              >
                <div className="sidebar-profile-avatar" style={{ padding: 0, overflow: 'hidden' }}>
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <div className="sidebar-profile-info">
                  <span className={classNames('sidebar-profile-name', isLongName ? 'sidebar-profile-name-long' : '')}>{displayName}</span>
                  <span className="sidebar-profile-role">{user?.companyName || 'Your company'}</span>
                </div>
              </button>
              {isProfileMenuOpen && (
                <div className="sidebar-profile-dropdown">
                  <Link
                    to="/dashboard/settings"
                    className="sidebar-profile-menu-item"
                  >
                    <CogIcon aria-hidden="true" />
                    <span>Settings</span>
                  </Link>
                  <Link
                    to="/dashboard/support"
                    className="sidebar-profile-menu-item"
                  >
                    <LifebuoyIcon aria-hidden="true" />
                    <span>Support</span>
                  </Link>
                  {footerNavigation.map((item) => (
                    item.name === 'Logout' ? (
                      <button
                        key={item.name}
                        type="button"
                        onClick={handleLogout}
                        className="sidebar-profile-menu-item sidebar-profile-logout"
                      >
                        <item.icon aria-hidden="true" />
                        <span>{item.name}</span>
                      </button>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="sidebar-profile-menu-item"
                      >
                        <item.icon aria-hidden="true" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Card */}
        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
}
